import { useEffect } from 'react';
import './how-it-works.styles.scss';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LEAGUE_TYPES } from '@shared/leagueTypes.js';
import { quadCutoffs } from '@shared/quads.js';
import { DRAFT_ROUNDS, LOTTERY_PICKS } from '@shared/draft.js';
import { CONTACTS_PER_DAY } from '@shared/recruiting.js';
import { CPU_ASKS_PER_DAY, MAX_OUTGOING, OFFER_DAYS } from '@shared/trades.js';
import ManagerShell from '@/components/HOME/manager-shell/manager-shell.component';
import UnauthLayout from '@/components/layout-unauth/unauth-layout.component';
import { BRAND_NAME } from '@/utils/brand';

// "How this game works": the player-facing guide. Open to everyone, signed
// in or not. Numbers come from the league presets so they stay accurate.
// Never explain how hidden traits are calculated or how big their effects
// are: managers shouldn’t be able to work them out.
const HowItWorksPage = () => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const location = useLocation();

    // Jump to a section when linked with #section (e.g. from a "?" link).
    useEffect(() => {
        if (!location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (el) setTimeout(() => el.scrollIntoView({ block: 'start' }), 0);
    }, [location.hash])

    const content = <HowItWorks signedIn={Boolean(userProfile)} />;
    return userProfile ?
        <ManagerShell title='How it works'>{content}</ManagerShell> :
        <UnauthLayout><div className='hiw-public'>{content}</div></UnauthLayout>;
}

const SECTIONS = [
    ['basics', 'The short version'],
    ['start', 'Getting started'],
    ['types', 'College and pro leagues'],
    ['gameday', 'Game days'],
    ['lineup', 'Your lineup'],
    ['ratings', 'Player ratings'],
    ['chemistry', 'Chemistry'],
    ['strategy', 'Strategy'],
    ['game-plans', 'Scouting and game plans'],
    ['hidden', 'Hidden traits'],
    ['rankings', 'Rankings'],
    ['postseason', 'The postseason'],
    ['rivalries', 'Rivalries and payback'],
    ['injuries', 'Injuries'],
    ['recruiting', 'College recruiting'],
    ['offseason', 'The offseason'],
    ['contracts', 'Pro contracts'],
    ['draft', 'The pro draft'],
    ['trades', 'Pro trades'],
    ['faq', 'Questions'],
];

const RATINGS = [
    ['3-point shooting', 'Making shots from behind the arc.'],
    ['Mid-range shooting', 'Making jump shots inside the arc.'],
    ['Driving', 'Getting to the basket and finishing there.'],
    ['Passing', 'Setting up teammates, which leads to assists and better shots.'],
    ['Offensive rebounding', 'Grabbing your own team\'s misses for second chances.'],
    ['Offensive IQ', 'Taking good shots and holding on to the ball.'],
    ['Perimeter defense', 'Staying in front of guards and contesting outside shots.'],
    ['Steals', 'Taking the ball away.'],
    ['Defensive IQ', 'Being in the right place on defense.'],
    ['Interior defense', 'Defending near the basket.'],
    ['Shot blocking', 'Blocking shots.'],
    ['Defensive rebounding', 'Ending the other team\'s possession by grabbing their misses.'],
];

const HowItWorks = ({ signedIn }) => {
    const college = LEAGUE_TYPES.college;
    const pro = LEAGUE_TYPES.pro;
    const cut = quadCutoffs(college.teamCount);
    const confRounds = Math.ceil(Math.log2(college.postseason.conferenceTournamentSize));
    const tourneyRounds = Math.log2(college.postseason.fieldSize);
    const proPlayoffDays = Math.ceil(pro.postseason.seriesLengths.reduce((s, n) => s + n, 0) / pro.gameDaysPerRealDay);
    const proRegularDays = Math.ceil(pro.gamesPerTeam / pro.gameDaysPerRealDay);
    const quadRange = (list, q) => (q === 3 ? `${list[2] + 1}+` : `${q === 0 ? 1 : list[q - 1] + 1}–${list[q]}`);

    return (
        <div className='how-it-works'>
            <header className='g-card hiw-intro'>
                <h1>How {BRAND_NAME} works</h1>
                <p>
                    You run a basketball team. You pick who plays, the games are played for you every day,
                    and you follow along through box scores, standings and the postseason. Here is everything you need to know.
                </p>
                <nav className='hiw-toc' aria-label='Sections'>
                    {SECTIONS.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
                </nav>
            </header>

            <Section id='basics' title='The short version'>
                <ul>
                    <li>You manage <b>one team</b> in a league. Every other team is run by another player or by the computer.</li>
                    <li>You set your <b>lineup</b>: who starts and who comes off the bench.</li>
                    <li>Games are <b>played automatically once a day</b>, possession by possession, and you get a full box score and play-by-play.</li>
                    <li>Talent matters, but so does <b>chemistry</b>: a team that fits together can beat a more talented team that doesn’t.</li>
                    <li>The season ends with a <b>postseason</b> and a champion.</li>
                </ul>
            </Section>

            <Section id='start' title='Getting started'>
                <ol>
                    <li>Sign in and create your profile.</li>
                    <li>Open <b>Leagues</b> and pick a league with open teams.</li>
                    <li>Choose a team. You can see each team’s record, its chemistry grade and, in college leagues, how strong the program is.</li>
                    <li>Set your lineup, then check back each day to see how it went.</li>
                </ol>
                <p>You can manage one team per league. You can give your team up at any time from your team page; it goes back to being run by the computer, and someone else can take it.</p>
                {!signedIn && <p><Link to='/signin' className='g-button primary'>Sign in to play</Link></p>}
            </Section>

            <Section id='types' title='College and pro leagues'>
                <p>There are two kinds of league. They play the same way; the difference is in how big they are and how even the teams are.</p>
                <div className='table-scroll'>
                    <table className='g-table hiw-table'>
                        <thead>
                            <tr><th></th><th>College</th><th>Pro</th></tr>
                        </thead>
                        <tbody>
                            <tr><td>Teams</td><td>About {college.teamCount}, in conferences</td><td>About {pro.teamCount}, in {pro.conferenceCount} conferences</td></tr>
                            <tr><td>Regular season</td><td>{college.gamesPerTeam} games</td><td>{pro.gamesPerTeam} games</td></tr>
                            <tr><td>Roster</td><td>{college.rosterSize} players</td><td>{pro.rosterSize} players</td></tr>
                            <tr><td>Game length</td><td>{college.minutesPerGame} minutes, two halves</td><td>{pro.minutesPerGame} minutes, four quarters</td></tr>
                            <tr><td>Talent</td><td>Big gaps between strong and weak programs</td><td>Teams are much closer</td></tr>
                            <tr><td>Careers</td><td>Four years of eligibility</td><td>Players age, peak and decline</td></tr>
                            <tr><td>Postseason</td><td>Conference tournaments, then a {college.postseason.fieldSize}-team national tournament</td><td>{pro.postseason.fieldSize}-team playoffs with best-of series</td></tr>
                        </tbody>
                    </table>
                </div>
                <h3>A season at a glance</h3>
                <Calendar label='College' parts={[
                    ['Regular season', college.gamesPerTeam, 'reg'],
                    ['Conf. tournaments', confRounds, 'conf'],
                    ['Selection', 1, 'sel'],
                    ['National tournament', tourneyRounds, 'post'],
                ]} />
                <Calendar label='Pro' parts={[
                    ['Regular season', proRegularDays, 'reg'],
                    ['Playoffs (up to)', proPlayoffDays, 'post'],
                ]} />
                <p className='meta'>Bar lengths are days. Both kinds of league take roughly six weeks.</p>
            </Section>

            <Section id='gameday' title='Game days'>
                <p>
                    Games are played automatically every day at the league’s game times. College leagues play one game day a day, usually
                    in the evening. Pro leagues play two, one in the afternoon and one in the evening, so their longer schedule fits in about
                    the same number of real days. Your team page shows when your next game tips off, in your own time zone.
                </p>
                <p>
                    Each game is played out possession by possession: shots, passes, turnovers, fouls and free throws, rebounds,
                    blocks and steals. Players get tired and go to the bench, and anyone who reaches the foul limit
                    ({college.game.foulOutLimit} in college, {pro.game.foulOutLimit} in pro) fouls out. The home team has a small advantage.
                </p>
                <p>After each game you can read the <b>box score</b> and the full <b>play-by-play</b>. Standings, stat leaders and player game logs update every day.</p>
                <p>
                    <b>Watch your games.</b> When your team has played, the app asks before showing you the result: watch the game unfold
                    play by play (quick, normal or slow, about 1, 4 or 10 minutes, with pause and skip, and the game stats or the matchup a tap away), or go straight to the result.
                    It remembers what you&apos;ve seen on every device. You can turn the question off on your team page.
                </p>
                <p>
                    <b>League news.</b> Each game day&apos;s biggest stories make the <b>News</b> page: triple-doubles, big scoring nights,
                    record-setting games, game-winners and comebacks, upsets, rivalry results, winning streaks and postseason moments.
                    There are a few headlines a day for every conference and the best of them for the whole league, and a My team tab
                    for your own team&apos;s headlines. News from the current and previous season is kept.
                </p>
            </Section>

            <Section id='lineup' title='Your lineup'>
                <p>The lineup is the main thing you control. At each of the five positions (point guard, shooting guard, small forward, power forward and center) you choose:</p>
                <ul>
                    <li><b>A starter</b>, who plays the most minutes.</li>
                    <li><b>Up to three backups</b>, in order. When a player gets tired, fouls out or gets hurt, the next player listed comes in.</li>
                </ul>
                <p>A few things happen on their own:</p>
                <ul>
                    <li>Players with more <b>stamina</b> stay on the floor longer before they need a rest.</li>
                    <li>Late in a <b>close game</b>, your starters stay in.</li>
                    <li><b>End-of-game coaching.</b> In the last few minutes your team plays the score and the clock: behind by a lot, it hurries and shoots more threes; behind on defense, it fouls to stop the clock (going after the worst free throw shooter) once time is getting short; ahead, it runs the shot clock down; tied, it holds for the last shot.</li>
                    <li><b>Injured players</b> are skipped. Your team page tells you who will start in their place.</li>
                    <li>Use the <b>Compare</b> button on the lineup page to see everyone who can play a position side by side.</li>
                </ul>
                <p>
                    <b>Playing out of position costs something.</b> A player is at their best at their own position and nearly as good at their second one.
                    The further from home, the bigger the drop: a point guard at center plays like a much worse player. The lineup page shows each
                    player’s overall at the position they’re listed, <span className='hiw-lower'>in red</span> when it’s out of position.
                    Your starters play where you list them. Once substitutes come in, the five on the floor shift into the positions that suit them best.
                </p>
                <p><b>Size matters too.</b> Taller players at a position block more shots and grab more rebounds, but they’re a step slower.</p>
                <p>Your lineup stays the same for every game until you change it. Teams run by the computer pick their lineups by overall rating.</p>
            </Section>

            <Section id='ratings' title='Player ratings'>
                <p>Players are rated from 1 to 99. <b>Overall</b> sums up how good a player is at their position, and <b>Offense</b> and <b>Defense</b> average the ratings below. <b>Stamina</b> (1 to 10) is how long they can play before tiring.</p>
                <dl className='hiw-ratings'>
                    {RATINGS.map(([name, text]) => (
                        <div key={name}><dt>{name}</dt><dd>{text}</dd></div>
                    ))}
                </dl>
                <p>College players show their class (freshman to senior). Pro players show their age.</p>
            </Section>

            <Section id='chemistry' title='Chemistry'>
                <p>
                    Chemistry is how well your players fit together. It doesn’t change anyone’s ratings. Instead it changes how the five players
                    on the floor play as a group: how well they shoot, how well they defend, how often they turn the ball over and how well they move it.
                </p>
                <p className='hiw-callout'>The better the chemistry, the better the team plays.</p>
                <p>
                    That means talent isn’t everything. A team with good chemistry can beat a more talented team that doesn’t fit together.
                </p>
                <p>
                    Your team gets a <b>chemistry grade</b> from A to F. The grade covers your whole roster, so it won’t tell you which player
                    is helping or hurting. Finding the combinations that work is part of the game: try different lineups and watch how they play.
                </p>
            </Section>

            <Section id='strategy' title='Strategy'>
                <p>
                    On the Strategy page you choose how your team usually plays. You can change it any day, postseason included, and you can
                    set a different plan for any of your next few games (see <a href='#game-plans'>Scouting and game plans</a>).
                </p>
                <ul>
                    <li><b>Offense:</b> motion (keep the ball moving), pick-and-roll (through your point guard and a big), post-up (feed the bigs), five-out (spread the floor and shoot threes), Princeton (patient cutting for backdoor layups) or isolation (let your best scorer go one-on-one).</li>
                    <li><b>Defense:</b> man-to-man, zone (protects the paint, gives up threes), press (forces turnovers but tires your players), pack-line (man-to-man that sags into the lane: stops drives, dares them to shoot) or switch (switch every screen; needs five players who can guard anyone).</li>
                    <li><b>Pace:</b> slow, normal or fast. A fast team and a slow team meet somewhere in between. Fast means more possessions and more running.</li>
                    <li><b>Rotation:</b> tight (your best seven, or eight in the pros, play nearly everything), standard (nine) or deep (eleven). Tired players play worse and get hurt more often, so a tight rotation at a fast pace needs players with stamina.</li>
                </ul>
                <p>
                    Two things decide whether a strategy works. The first you can see: how well your players’ ratings suit it (shooters for five-out,
                    shot-blockers for a zone, quick guards for a press). The Strategy page marks a good or poor roster fit.
                </p>
                <p>
                    Your opponent’s choices matter too: some offenses do well against some defenses and badly against others, the way a team of shooters
                    loves to see a zone. Nobody hands you the list. Read a team’s scouting report, watch your games (a game page notes when a matchup
                    decided it) and learn what beats what. Countering is a gamble, though: a system your team hasn’t run much doesn’t play as well.
                </p>
                <p>
                    The second you can’t see. Every player is more comfortable in some systems than others, and every coach (you included) teaches some
                    systems better than others. It matters as much as chemistry. You find it the way a real coach does: after each game you’ll see how
                    your players looked in what you ran. It starts vague and gets clearer the longer you run a system, and the longer you run it, the better
                    your team gets at it. When your roster changes in the offseason, the right system can change too.
                </p>
            </Section>

            <Section id='game-plans' title='Scouting and game plans'>
                <p>
                    Before each game you can study your opponent and change your plan for that one game. Nobody else can see your plan
                    until the game tips off, and other managers&apos; plans are just as secret, so scouting is about reading tendencies, not answers.
                </p>
                <ul>
                    <li>
                        <b>Scouting report:</b> open it from the Next game card, the Strategy page or any team page. It shows what a team has run
                        this season, how it plays (pace, threes, free throws, turnovers) with league ranks, who takes its shots, how it has done
                        against each defense and offense, and its last three games.
                    </li>
                    <li>
                        <b>Game plans:</b> on the Strategy page, set the offense, defense and pace for any of your next three days of games.
                        A game without a plan uses your usual strategy, and a plan only lasts for its game. Remember that running something
                        your team hasn&apos;t practiced is a gamble: teams get better at a system the longer they run it.
                    </li>
                    <li>
                        <b>Key on a player:</b> your defense focuses on one opponent. That player gets fewer shots and makes fewer of them,
                        but the other four get better looks. It pays off against a team that leans on one scorer, does little against a
                        balanced team, and hurts you if you pick the wrong player. Computer coaches key on players too, mostly against teams
                        built around a star.
                    </li>
                </ul>
            </Section>

            <Section id='hidden' title='Hidden traits'>
                <p>
                    Every player has a few traits you can’t see, including how they fit your team’s chemistry and how much they could still improve (their potential).
                    These are hidden on purpose and they do matter. You learn about them the way a real coach would: by watching your team play.
                </p>
            </Section>

            <Section id='rankings' title='Rankings'>
                <p>
                    Every league ranks its teams, starting before the first game. The <b>preseason ranking</b> is a projection based on
                    player ability, the team’s chemistry grade, the strength of its schedule and, in college, the program’s prestige.
                    Once games start, results count for more and more, and by the end of the season the ranking is mostly about how teams actually played.
                    Every team page has a chart of its ranking over the season.
                </p>
                <ul>
                    <li><b>College: the Top 25.</b> The 25 best teams show their rank next to their name all around the league, like “Stoneborough State (2)”.</li>
                    <li><b>Conference reputation (college).</b> Each program has a prestige of one to five stars, and a conference’s prestige is the average of its teams’ (shown next to its name in the standings). All season long the Top 25 gives teams from prestigious conferences a little benefit of the doubt, as real polls do. It never decides Selection Day: the tournament field is picked on results.</li>
                    <li><b>Pro: Power Rankings.</b> The same idea, shown on the Power Rankings page and team pages.</li>
                </ul>
                <h3>Team rating and quads (college)</h3>
                <p>
                    Selection Day doesn’t use the Top 25. It uses a <b>team rating</b> based only on results: scoring margin,
                    adjusted for how strong the opponents were and where the games were played, plus credit for winning. Winning by a huge margin doesn’t count extra.
                </p>
                <p>
                    <b>Quad records</b> sort every win and loss by how good the opponent was and where you played. Beating a top team on the road
                    is a Quad 1 win; losing at home to a weak team is a Quad 4 loss. In a {college.teamCount}-team league, the cutoffs are:
                </p>
                <div className='table-scroll'>
                    <table className='g-table hiw-table'>
                        <thead>
                            <tr><th>Opponent rank</th><th>Home</th><th>Neutral</th><th>Away</th></tr>
                        </thead>
                        <tbody>
                            {[0, 1, 2, 3].map(q => (
                                <tr key={q}>
                                    <td>Quad {q + 1}</td>
                                    <td>{quadRange(cut.home, q)}</td>
                                    <td>{quadRange(cut.neutral, q)}</td>
                                    <td>{quadRange(cut.away, q)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p>The team rating and quad records decide who makes the national tournament and where they are seeded.</p>
            </Section>

            <Section id='postseason' title='The postseason'>
                <h3>College</h3>
                <ol>
                    <li><b>Conference tournaments.</b> The top teams in each conference play a single-elimination tournament, seeded by conference record. Top seeds may get a bye. The winner earns an <b>automatic bid</b>.</li>
                    <li><b>Selection Day.</b> The rest of the {college.postseason.fieldSize}-team field is chosen by ranking and quad record. Teams are seeded and spread across four regions so the best teams meet as late as possible.</li>
                    <li><b>The national tournament.</b> Single elimination at neutral sites, from the first round through the Final Four to the championship.</li>
                </ol>
                <h3>Pro</h3>
                <p>
                    Usually {pro.postseason.fieldSize} teams make the playoffs (a league can choose 4, 8, 12 or 16), split evenly between the conferences:
                    the best records in each conference get in, and each conference plays its own side of the bracket, so the conference champions meet in the Finals.
                    With 12 teams the top two in each conference get a first-round bye. Each round is a best-of series ({pro.postseason.seriesLengths.join(', ')} games),
                    and the higher seed gets the extra home game. Playoff games don’t change regular-season records.
                </p>
                <p>
                    During the season, the Conference tournaments (college) or Playoffs (pro) page shows the race: each conference in seeding order,
                    the line for who gets in, and the next four teams below it. The national tournament has its own page.
                </p>
            </Section>

            <Section id='rivalries' title='Rivalries and payback'>
                <ul>
                    <li><b>Rivals.</b> Every team has two or three rivals in its conference, usually nearby, and keeps them for good. Some college rivalries have a name. Your team page shows each rival and the all-time series.</li>
                    <li><b>Rivalry games.</b> Throw out the records: in a rivalry game, the team with less talent plays with a little extra fire.</li>
                    <li><b>Payback games.</b> A rematch with a team that beat you in your last meeting this season, or that ended your last season in the postseason. The team out for payback plays with a little extra focus.</li>
                    <li>Both edges are small: the better team still usually wins. Schedules show rivalry and payback games, and every team keeps its record in them.</li>
                </ul>
            </Section>

            <Section id='injuries' title='Injuries'>
                <p>
                    Players can get hurt during a game. An injured player misses a set number of games, counting down each time the team plays.
                    Your team page lists everyone who is hurt and tells you who will start in place of an injured starter. Injured players stay in
                    your lineup and return to it automatically when they’re healthy.
                </p>
            </Section>

            <Section id='recruiting' title='College recruiting'>
                <p>
                    Each college season you recruit next year’s freshmen from a class of high school players. Recruiting runs from the preseason
                    to <b>signing day</b>, the last day of the regular season. You can give as many scholarships as you have seniors graduating
                    (minus recruits who already committed).
                </p>
                <ul>
                    <li><b>The class.</b> Every recruit has a position, height, home state and a star rating from one to five. Stars are the scouting services’ read of the recruit’s current ability. You only see ratings as ranges: a first look shows the overall within about a dozen points.</li>
                    <li><b>Offers.</b> A recruit only commits to a school that offered a scholarship. You can have twice as many offers out as you have scholarships, plus one, and you can withdraw one any time.</li>
                    <li><b>Contacts.</b> Each day you have {CONTACTS_PER_DAY} contacts. Use one to <b>pitch</b> a recruit (playing time, winning, development or program prestige; the reply hints at what the recruit values) or to <b>scout</b> the recruit, which narrows the rating ranges. Three visits give you the ratings almost exactly. Your scouting reports and replies are yours alone.</li>
                    <li><b>What recruits want.</b> Each cares about playing time at their position, winning, development and prestige in their own mix. Recruits also like staying close to home, and your <b>rivalry and payback wins</b> this season impress them. The best recruits expect a big program, so landing one with a small school takes work.</li>
                    <li><b>Where to start.</b> The Recruiting page shows your team&apos;s needs for next season, position by position. Each recruit lists the schools they&apos;re most interested in (high, medium or low); <b>Interested in us</b> shows every recruit with your school on that list.</li>
                    <li><b>Commitments.</b> Interest is public, as are offers and commitments. Recruits start committing a few days into the season, the best ones usually later. On signing day everyone with an offer picks a school.</li>
                    <li><b>Position limits.</b> In the Team needs table, set the most recruits you want at a position. Once that many commit there, your other offers at that position are withdrawn. That lets you offer a backup without risking both players committing and using two scholarships.</li>
                    <li><b>Pulling a scholarship.</b> Once a season, before signing day, you can pull the scholarship of a recruit who committed to you. It costs you: the recruit never considers your school again, every other recruit&apos;s interest in your school drops for the rest of the season, and the league news reports it. Computer coaches do it now and then too.</li>
                    <li><b>Arrival.</b> Your class joins when players develop in the offseason. Recruits who never signed fill any spots still open after the transfer portal, and generated walk-ons fill the rest.</li>
                </ul>
                <p><b>Pitching tips</b> (pitches in the transfer portal work the same way):</p>
                <ul>
                    <li><b>Read the first reply.</b> The first time you make a pitch, the reply tells you where that selling point ranks for the recruit: “That’s exactly what I’m looking for” is their main thing, “That matters to me” is second, “That’s nice, but it’s not my main thing” is third and “That’s not really what I care about” is last. The main thing counts for about half of what they want.</li>
                    <li><b>Then listen to how they sound.</b> When you repeat a pitch, the recruit answers in their own words, and the tone tells you which way their interest in your school has moved since you last talked: warming up to you, still listening, tired of hearing it, or cooling off. That counts everything, not just the pitch: a losing streak or another recruit committing at their position can cool them even after a good pitch.</li>
                    <li><b>Repeating a pitch helps, but less each time.</b> Every repeat of the same pitch is worth about half the one before. Two pitches on the main thing do most of the work; by the third, a first pitch on their second choice is usually worth more. After a few, spend your contacts on other recruits.</li>
                    <li><b>You have to back it up.</b> A pitch only counts as much as your school can deliver it. Winning means little from a losing team, prestige means nothing from a one-star program, and playing time is a hard sell when you already have a better player (or other recruits) coming at their position. Development is believable from any school. The first reply never tells you whether your school is good at it, only whether the recruit cares; a repeat that falls flat on something they care about is a sign you can&apos;t back it up.</li>
                    <li><b>Your school sells itself, too.</b> Even without a pitch, recruits weigh what you really offer: minutes at their position, your record, your prestige, home and big wins. Pitches add to that; they don&apos;t replace it.</li>
                    <li><b>Pitch early, and offer.</b> Pitches never wear off, but recruits start committing a few days in, and they pick the offering school they like most. What matters is how far you lead the next school, so get there before your rivals do.</li>
                </ul>
            </Section>

            <Section id='offseason' title='The offseason'>
                <p>After the champion is crowned, the league moves through the offseason one step a day:</p>
                <ol>
                    <li>
                        <b>The record books.</b> The season is saved: final standings and rankings, the postseason, stat leaders and every box score. Each team’s page gets a line in its history, and each player’s page a line in their career.
                        Awards are handed out for the league and for each conference: <b>Player of the Year</b>, <b>Offensive</b> and <b>Defensive Player of the Year</b> and <b>Sixth Player of the Year</b> (the best player who started no more than a quarter of their games).
                        They go by the box score and, for Player of the Year, how the team did. Regular-season and tournament conference champions are recorded too, and the league’s <b>record book</b> keeps the ten best seasons and single games in each category.
                    </li>
                    <li>
                        <b>The transfer portal</b> (college). For the first two days you can cut up to three returning players; they go into the portal.
                        Then seniors graduate and the portal opens. Good players who didn’t get the minutes they deserved, players whose team finished well
                        below its preseason ranking and players who never fit in are the likeliest to leave. For seven days you can send three pitches a day:
                        playing time, winning, development or program prestige. Each player cares about some of these more than others: the first reply to each pitch hints at
                        which, and later replies tell you whether they&apos;re warming up to you. Every school’s interest (low, medium or high) is public. From day 3 players start committing; when the portal closes, everyone
                        left picks the school with room they like best.
                    </li>
                    <li><b>Player development.</b> Every player gets a year older. Some improve a lot, some a little, and older pros start to decline. College seniors graduate and the recruiting class takes their places; pros retire, and new pros arrive through the draft and free agency. Your team page lists who left, who arrived and how much each returning player changed.</li>
                    <li><b>A new season.</b> A new schedule and preseason rankings, and records and stats start over.</li>
                </ol>
                <p>Your lineup keeps the players who stayed. Check it before the first game to place your new players.</p>
            </Section>

            <Section id='contracts' title='Pro contracts'>
                <p>
                    Pro players have contracts: a salary and the years left. Every team’s payroll must stay under a hard cap of $140M,
                    rosters carry 12 to 15 players, and salaries run from $1.2M to $42M.
                </p>
                <ul>
                    <li><b>Re-signing.</b> After players develop, you have two days to offer your expiring players new contracts. Each one answers right away, one offer a day. A no comes with a hint at what the player wants.</li>
                    <li><b>The draft</b> follows (see below). Rookie contracts may go over the cap.</li>
                    <li><b>Free agency.</b> Then everyone left unsigned, plus undrafted prospects and other new players, hits the market for seven days. Make offers that fit your cap room and pitch players (playing time, winning, development). Each player weighs money against what they value most; their replies hint at what that is. From day 3 players start signing; when it closes, everyone with a fair offer signs.</li>
                    <li><b>During the season</b> you can sign any remaining free agent at their asking price for a year, or release a player. A released player’s salary still counts against your cap for the rest of the season.</li>
                    <li>Any team below 12 players when games start gets minimum-salary free agents automatically (the only time a team may go over the cap). A team above 15 releases its lowest-rated players.</li>
                </ul>
            </Section>

            <Section id='draft' title='The pro draft'>
                <p>
                    Every pro offseason has a {DRAFT_ROUNDS}-round draft of young players (mostly 19 to 22).
                </p>
                <ul>
                    <li>
                        <b>The lottery</b> is held as soon as the season goes into the record books. Only teams that missed the playoffs are in it.
                        The top {LOTTERY_PICKS} picks are drawn, and the worse a team’s record, the better its chances: the worst teams have about a
                        1-in-7 shot at the first pick (in a league with 14 lottery teams). After the drawing, the rest of round 1 goes worst record first,
                        with playoff teams last; round 2 goes worst record first.
                    </li>
                    <li>
                        <b>Your draft queue.</b> From the lottery until draft day, rank the prospects you want on the Draft page. On draft day every pick
                        is made at once: at each of your picks you get the highest player on your queue who’s still there. If nobody on it is left,
                        your scouts take the best player available.
                    </li>
                    <li>
                        <b>Rookie contracts.</b> First-round picks sign for four years on a scale (the first pick earns the most), second-rounders for
                        two years at the minimum. Undrafted prospects become free agents.
                    </li>
                    <li>You only see a prospect’s current ratings. Younger players usually have more growth ahead, but not always.</li>
                </ul>
            </Section>

            <Section id='trades' title='Pro trades'>
                <p>
                    Pro teams can trade players and draft picks on the Trades page, from the preseason to the <b>trade deadline</b>
                    (two-thirds of the way through the regular season) and again during free agency, after the draft.
                </p>
                <ul>
                    <li><b>What can move.</b> Any players under contract, and 1st- and 2nd-round picks in the next two drafts. A traded pick keeps its place: it’s where the original team finishes.</li>
                    <li><b>The rules.</b> After the trade both teams must be under the cap (or no further over it than before) and within the roster limits. The Trades page checks as you build the deal.</li>
                    <li><b>Computer-run teams</b> answer right away. They look at ability, age and contract (never hidden traits): a team going for the title wants help now; a rebuilding team wants young players and picks. They only say yes when they come out ahead, and their reply tells you how close you are. You can make {CPU_ASKS_PER_DAY}{' '}offers to computer teams a day.</li>
                    <li><b>Other managers</b> get your offer to accept or decline. An offer waits {OFFER_DAYS} days, and you can have {MAX_OUTGOING} waiting at once.</li>
                    <li>Now and then a computer team offers you a trade. It’s on your team page and the Trades page.</li>
                </ul>
            </Section>

            <Section id='faq' title='Questions'>
                <Faq q="Why didn’t my starter start?">
                    They were probably injured. The next player listed at that position started instead. Your team page shows who is hurt and who is filling in.
                </Faq>
                <Faq q="Can I see a player’s chemistry?">
                    No. You only see your team’s overall grade. Working out which combinations play well together is part of the challenge.
                </Faq>
                <Faq q='When are games played?'>
                    Every day at the league’s game times: once a day in college, twice in pro. Your team page shows when your next
                    game tips off. You don’t need to be online; set your lineup ahead of time and check the results later.
                </Faq>
                <Faq q='What happens if I stop playing?'>
                    Your lineup keeps being used. If you give up your team, the computer takes over and another player can claim it.
                </Faq>
                <Faq q='Do I have to change my lineup every day?'>
                    No. Your lineup stays until you change it. It’s worth a look when someone gets hurt.
                </Faq>
            </Section>
        </div>
    )
}

const Section = ({ id, title, children }) => (
    <section id={id} className='g-card hiw-section'>
        <h2>{title}</h2>
        {children}
    </section>
);

const Faq = ({ q, children }) => (
    <details className='hiw-faq'>
        <summary>{q}</summary>
        <p>{children}</p>
    </details>
);

const Calendar = ({ label, parts }) => (
    <div className='hiw-calendar'>
        <div className='cal-label'>{label}</div>
        <div className='cal-bar'>
            {parts.map(([name, days, kind]) => (
                <div key={name} className={`cal-seg ${kind}`} style={{ flex: days }} title={`${name}: ${days} days`}>
                    <span>{name} · {days}</span>
                </div>
            ))}
        </div>
    </div>
);

export default HowItWorksPage
